import { SecretService, Tag } from '../../../../services/secret.ts';
import { ToastService } from '../../../../services/toast.ts';
import '../../../../components/library/ui/app-button.ts';
import '../../../../components/library/ui/app-modal.ts';

export class OrgTagsTab extends HTMLElement {
  private _tags: Tag[] = [];
  private _orgId: string = '';
  private _toastService = ToastService.getInstance();
  private _secretService = SecretService.getInstance();
  
  private _isModalOpen: boolean = false;
  private _editingTag: Tag | null = null;
  private _isSaving: boolean = false;

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ['org-id'];
  }

  set tags(val: Tag[]) {
    this._tags = val;
    this.render();
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string) {
    if (oldVal === newVal) return;
    if (name === 'org-id') {
      this._orgId = newVal;
    }
  }

  private _initialized: boolean = false;
  connectedCallback() {
    if (!this._initialized) {
      this.addEventListener('change', (e) => e.stopPropagation());
      this.addEventListener('input', (e) => e.stopPropagation());
      this._initialized = true;
    }
    this.render();
  }

  private _renderTimeout: any = null;
  render() {
    if (this._renderTimeout) return;
    this._renderTimeout = setTimeout(() => {
      this._renderInternal();
      this._renderTimeout = null;
    }, 0);
  }

  private _renderInternal() {
    this.innerHTML = `
      <style>
        .tags-container { padding: 24px 0; }
        .tags-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
        .tags-list { display: flex; flex-direction: column; gap: 12px; }
        .tag-item { 
          display: flex; justify-content: space-between; align-items: center; 
          padding: 16px; background: rgba(255, 255, 255, 0.02); 
          border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 8px; 
        }
        .tag-info { display: flex; align-items: center; gap: 16px; }
        .tag-color { width: 16px; height: 16px; border-radius: 50%; }
        .tag-name { font-weight: 500; font-family: var(--font-mono); }
        .tag-desc { color: var(--text-muted); font-size: 0.9rem; }
        .form-group { margin-bottom: 16px; }
        .form-group label { display: block; margin-bottom: 8px; color: var(--text-muted); font-size: 0.9rem; }
      </style>
      
      <div class="tags-container">
        <div class="tags-header">
          <div>
            <h2 style="margin: 0; font-size: 1.25rem;">Organization Tags</h2>
            <p style="margin: 4px 0 0; color: var(--text-muted); font-size: 0.9rem;">Manage tags that can be applied to secrets.</p>
          </div>
          <app-button id="btn-create-tag" icon="plus" size="sm" variant="white" square data-tooltip="Create Tag"></app-button>
        </div>

        ${this._tags.length === 0 ? `
          <div style="text-align: center; padding: 48px; border: 1px dashed rgba(255,255,255,0.1); border-radius: 8px; color: var(--text-muted);">
            No tags found. Create one to get started.
          </div>
        ` : `
          <div class="tags-list">
            ${this._tags.map(tag => `
              <div class="tag-item">
                <div class="tag-info">
                  <div class="tag-color" style="background-color: ${tag.metadata?.color || '#ffffff'}"></div>
                  <div>
                    <div class="tag-name">${tag.name}</div>
                    ${tag.metadata?.description ? `<div class="tag-desc">${tag.metadata.description}</div>` : ''}
                  </div>
                </div>
                <div style="display: flex; gap: 4px;">
                  <app-button variant="ghost" size="sm" class="btn-edit-tag" data-id="${tag.id}" icon="edit" data-tooltip="Edit Tag"></app-button>
                  <app-button variant="ghost" size="sm" class="btn-delete-tag" data-id="${tag.id}" icon="trash" data-tooltip="Delete Tag"></app-button>
                </div>
              </div>
            `).join('')}
          </div>
        `}
      </div>

      ${this._isModalOpen ? `
        <app-modal open size="sm">
          <div slot="header">${this._editingTag ? 'Edit Tag' : 'Create Tag'}</div>
          
          <div class="form-group">
            <label>Name</label>
            <input type="text" id="input-tag-name" value="${this._editingTag?.name || ''}" placeholder="e.g. production" style="width: 100%; padding: 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.2); color: white;">
          </div>
          <div class="form-group">
            <label>Description (Optional)</label>
            <input type="text" id="input-tag-desc" value="${this._editingTag?.metadata?.description || ''}" placeholder="Tag description" style="width: 100%; padding: 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.2); color: white;">
          </div>
          <div class="form-group">
            <label>Color</label>
            <input type="color" id="input-tag-color" value="${this._editingTag?.metadata?.color || '#ffffff'}" style="width: 100%; height: 40px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1); background: none; cursor: pointer;">
          </div>

          <div slot="actions">
            <app-button variant="ghost" size="sm" id="btn-cancel-modal" icon="x" data-tooltip="Cancel"></app-button>
            <app-button variant="primary" size="sm" id="btn-save-modal" icon="check" data-tooltip="Save" ${this._isSaving ? 'loading' : ''}></app-button>
          </div>
        </app-modal>
      ` : ''}
    `;

    this.setupListeners();
  }

  private setupListeners() {
    const createBtn = this.querySelector('#btn-create-tag');
    if (createBtn) {
      createBtn.addEventListener('click', () => {
        this._editingTag = null;
        this._isModalOpen = true;
        this.render();
      });
    }

    const editBtns = this.querySelectorAll('.btn-edit-tag');
    editBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset.id;
        this._editingTag = this._tags.find(t => t.id === id) || null;
        this._isModalOpen = true;
        this.render();
      });
    });

    const deleteBtns = this.querySelectorAll('.btn-delete-tag');
    deleteBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (!id) return;
        if (!confirm('Are you sure you want to delete this tag?')) return;
        try {
          await this._secretService.deleteTag(this._orgId, id);
          this._toastService.success('Tag deleted');
          this.dispatchEvent(new CustomEvent('tags-updated', { bubbles: true, composed: true }));
        } catch (err: any) {
          this._toastService.error(err.message || 'Failed to delete tag');
        }
      });
    });

    const cancelBtn = this.querySelector('#btn-cancel-modal');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        this._isModalOpen = false;
        this._editingTag = null;
        this.render();
      });
    }

    const saveBtn = this.querySelector('#btn-save-modal');
    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        const nameInput = this.querySelector('#input-tag-name') as HTMLInputElement;
        const descInput = this.querySelector('#input-tag-desc') as HTMLInputElement;
        const colorInput = this.querySelector('#input-tag-color') as HTMLInputElement;
        
        const name = nameInput?.value?.trim();
        const description = descInput?.value?.trim() || undefined;
        const color = colorInput?.value;

        if (!name) {
          this._toastService.error('Tag name is required');
          return;
        }

        this._isSaving = true;
        this.render();

        try {
          if (this._editingTag) {
            await this._secretService.updateTag(this._orgId, this._editingTag.id, name, color, description);
            this._toastService.success('Tag updated');
          } else {
            await this._secretService.createTag(this._orgId, name, color, description);
            this._toastService.success('Tag created');
          }
          this._isModalOpen = false;
          this._editingTag = null;
          this.dispatchEvent(new CustomEvent('tags-updated', { bubbles: true, composed: true }));
        } catch (err: any) {
          this._toastService.error(err.message || 'Failed to save tag');
        } finally {
          this._isSaving = false;
          this.render();
        }
      });
    }
  }
}

if (!customElements.get('org-tags-tab')) {
  customElements.define('org-tags-tab', OrgTagsTab);
}
